import {formatDistanceToNow} from 'date-fns';
import {CheckCircle2, ClipboardCheck, Film, NotebookPen, XCircle} from 'lucide-react';
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {type QaReviewItem} from '@/api/qaReview';
import {type Submission} from '@/api/submissions';
import {useSubmitQaReview} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {SensitiveMediaButton} from '@/components/shared/SensitiveMedia';
import {TruncatedText} from '@/components/shared/TruncatedText';
import {PlatformStatusCell, RowMarker} from '@/components/submissions/SubmissionsTable';
import {SubmissionStatusPill} from '@/components/submissions/SubmissionStatusPill';
import {showToast} from '@/lib/toast';

const COLUMN_COUNT = 9;

export function QaQueueTable({
  items,
  isLoading,
  onOpenReview
}: {
  readonly items: QaReviewItem[];
  readonly isLoading: boolean;
  /**
   Opens the detail review modal (video preview, full context, both outcomes inside) - triggered by
   clicking the item itself or the row's False Positive button, same as the QC review queue.
   */
  readonly onOpenReview: (item: QaReviewItem) => void;
}) {
  const submitReview = useSubmitQaReview();

  async function handleTruePositive(item: QaReviewItem) {
    try {
      await submitReview.mutateAsync({id: item.id, payload: {outcome: 'true_positive'}});
      showToast('Marked as true positive.');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to submit review.'), 'error');
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {/* The Submissions grid's columns, in its order, so a sampled submission reads the same here
              as it did there - most of all its Status Check, since QA samples delivered work. */}
          <TableHead>Delivery Status</TableHead>
          <TableHead>Status Check</TableHead>
          <TableHead>Content</TableHead>
          <TableHead>Policy / Risks</TableHead>
          <TableHead>Language / Location</TableHead>
          <TableHead>Notes</TableHead>
          <TableHead>Analyst</TableHead>
          <TableHead>Sampled</TableHead>
          <TableHead className="bg-card sticky right-0 text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
                <Spinner />
                Loading…
              </div>
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && items.length === 0 ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT} className="p-0">
              <Empty className="py-10">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ClipboardCheck />
                  </EmptyMedia>
                  <EmptyTitle>Queue clear</EmptyTitle>
                  <EmptyDescription>No pending QA items for this vertical.</EmptyDescription>
                </EmptyHeader>
              </Empty>
            </TableCell>
          </TableRow>
        ) : null}
        {items.map((item) => {
          const title = item.title ?? item.item_external_id ?? item.id;
          const handle = item.author_handle
            ? item.author_handle.startsWith('@')
              ? item.author_handle
              : `@${item.author_handle}`
            : null;
          const author = [item.author_name, handle === item.author_name ? null : handle]
            .filter(Boolean)
            .join(' ');
          return (
            <TableRow key={item.id}>
              <TableCell>
                <SubmissionStatusPill
                  status={item.current_delivery_state as Submission['action_status']}
                />
              </TableCell>
              <TableCell>
                <PlatformStatusCell sub={item.status_check} />
              </TableCell>
              <TableCell className="max-w-[320px]">
                <div className="flex items-center gap-3">
                  {item.thumbnail_url ? (
                    <SensitiveMediaButton
                      compact
                      label={`Review ${title}`}
                      src={item.thumbnail_url}
                      alt=""
                      className="bg-muted h-9 w-14 shrink-0 overflow-hidden"
                      imageClassName="h-full w-full object-cover"
                      onClick={() => {
                        onOpenReview(item);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      aria-label={`Review ${title}`}
                      className="bg-muted flex h-9 w-14 shrink-0 items-center justify-center overflow-hidden"
                      onClick={() => {
                        onOpenReview(item);
                      }}>
                      <Film className="text-muted-foreground size-4" />
                    </button>
                  )}
                  <div className="min-w-0">
                    <button
                      type="button"
                      className="hover:text-primary-ink block max-w-full truncate text-left text-xs font-medium transition-colors"
                      onClick={() => {
                        onOpenReview(item);
                      }}>
                      {title}
                    </button>
                    {item.title && item.item_external_id ? (
                      <p className="text-muted-foreground truncate font-mono text-xs">
                        {item.item_external_id}
                      </p>
                    ) : null}
                    {author ? (
                      <TruncatedText className="text-muted-foreground text-xs">
                        {author}
                      </TruncatedText>
                    ) : null}
                    <div className="mt-1 flex items-center">
                      <Badge variant="secondary">{item.submission.submission_type}</Badge>
                      {item.has_calibration_notes ? (
                        <RowMarker icon={NotebookPen} label="Has calibration notes" />
                      ) : null}
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="max-w-[200px]">
                <div className="flex flex-col items-start gap-1">
                  <Badge variant="outline">{item.original_policy_violation}</Badge>
                  <TruncatedText className="text-muted-foreground max-w-full text-xs">
                    {item.submission.risks.join(', ') || item.original_risk}
                  </TruncatedText>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground max-w-[160px]">
                <TruncatedText>{item.submission.language.join(', ') || '—'}</TruncatedText>
                <TruncatedText className="text-xs">
                  {item.submission.location.join(', ') || '—'}
                </TruncatedText>
              </TableCell>
              <TableCell className="max-w-[280px]">
                {item.submission.notes ? (
                  <TruncatedText>{item.submission.notes}</TruncatedText>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground max-w-[180px] text-xs">
                <TruncatedText>{item.analyst_email}</TruncatedText>
              </TableCell>
              <TableCell className="text-muted-foreground text-xs">
                {formatDistanceToNow(new Date(item.created_at), {addSuffix: true})}
              </TableCell>
              <TableCell className="bg-card sticky right-0 text-right">
                <div className="flex items-center justify-end gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    title="True positive"
                    disabled={submitReview.isPending}
                    onClick={() => void handleTruePositive(item)}>
                    {submitReview.isPending ? <Spinner /> : <CheckCircle2 />}
                    {/* Icons only on narrower screens, so the pinned column doesn't cover Analyst
                        and Sampled; the hidden text still names the button. */}
                    <span className="max-2xl:sr-only">True positive</span>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    title="False positive"
                    disabled={submitReview.isPending}
                    onClick={() => {
                      onOpenReview(item);
                    }}>
                    <XCircle />
                    <span className="max-2xl:sr-only">False positive</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
